import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import CountdownTimer from '../../components/feature/CountdownTimer';
import SEOHead from '../../components/feature/SEOHead';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'geral',
    label: 'Geral',
    icon: 'ri-question-line',
    color: 'from-petrol-500 to-petrol-700',
    items: [
      {
        question: 'O que é a IBX Brasil?',
        answer: 'A IBX Brasil é uma empresa especializada em gestão de riscos e contratação profissional de seguros, com mais de 25 anos de experiência no mercado. Atuamos como consultores estratégicos, analisando o perfil de risco de cada cliente e negociando as melhores condições junto às seguradoras.'
      },
      {
        question: 'Qual a diferença entre a IBX Brasil e uma corretora comum?',
        answer: 'Enquanto corretoras comuns apenas intermediam a venda de apólices, a IBX Brasil realiza uma análise técnica aprofundada do seu perfil de risco, negocia ativamente com múltiplas seguradoras, acompanha a vigência do contrato e atua na recuperação de sinistros. Nosso foco é resultado e economia real para o cliente.'
      },
      {
        question: 'A IBX Brasil atende pessoas físicas e jurídicas?',
        answer: 'Sim. Atendemos tanto pessoas físicas quanto empresas de todos os portes. Para pessoas físicas, trabalhamos principalmente com seguro automóvel e vida. Para empresas, oferecemos soluções completas de gestão de riscos, incluindo vida empresarial, frota, patrimônio e responsabilidade civil.'
      },
      {
        question: 'Em quais regiões do Brasil a IBX Brasil atua?',
        answer: 'Nossa sede está em Vitória da Conquista, Bahia, mas atendemos clientes em todo o território nacional. Graças ao atendimento remoto e à nossa rede de parceiros, conseguimos oferecer o mesmo nível de qualidade independentemente da localização do cliente.'
      },
      {
        question: 'Como posso entrar em contato com a IBX Brasil?',
        answer: 'Você pode nos contatar pelo WhatsApp (77) 98808-0505, pelo e-mail contato@ibxbrasil.com.br ou pelo formulário em nossa página de contato. Nosso horário de atendimento é de segunda a sexta, das 8h às 18h, e sábados das 8h às 12h.'
      }
    ]
  },
  {
    id: 'contratacao',
    label: 'Contratação',
    icon: 'ri-file-text-line',
    color: 'from-strategic-green-500 to-strategic-green-700',
    items: [
      {
        question: 'Como funciona o processo de contratação de seguros pela IBX Brasil?',
        answer: 'O processo segue 5 etapas: (1) Diagnóstico — levantamento completo do seu perfil de risco; (2) Análise Técnica — avaliação das coberturas necessárias; (3) Negociação — cotação e negociação com múltiplas seguradoras; (4) Implementação — formalização e emissão da apólice; (5) Acompanhamento — monitoramento contínuo durante toda a vigência.'
      },
      {
        question: 'Quanto tempo leva para contratar um seguro pela IBX Brasil?',
        answer: 'O prazo varia conforme a complexidade do risco. Para seguros de automóvel, o processo pode ser concluído em 24 a 48 horas. Para seguros empresariais mais complexos, como vida em grupo ou gestão de contratos, o processo pode levar de 5 a 15 dias úteis, garantindo uma análise completa e a melhor negociação.'
      },
      {
        question: 'Preciso cancelar meu seguro atual para contratar com a IBX Brasil?',
        answer: 'Não necessariamente. Realizamos uma análise do seu contrato atual e, se identificarmos que ele está adequado, informamos isso com transparência. Caso haja oportunidade de melhoria, orientamos sobre o momento ideal para a migração, evitando perdas financeiras ou lacunas de cobertura.'
      },
      {
        question: 'A IBX Brasil trabalha com quais seguradoras?',
        answer: 'Trabalhamos com as principais seguradoras do mercado brasileiro, incluindo Porto Seguro, Bradesco Seguros, SulAmérica, Allianz, Tokio Marine, Mapfre, Zurich, HDI e outras. Essa ampla rede nos permite negociar as melhores condições para cada perfil de cliente.'
      },
      {
        question: 'É possível contratar mais de um tipo de seguro ao mesmo tempo?',
        answer: 'Sim, e isso é inclusive recomendado. Ao centralizar a gestão de todos os seus seguros na IBX Brasil, conseguimos uma visão integrada dos seus riscos, evitar sobreposições de cobertura e negociar melhores condições com as seguradoras por volume de negócios.'
      }
    ]
  },
  {
    id: 'custos',
    label: 'Custos e Economia',
    icon: 'ri-money-dollar-circle-line',
    color: 'from-action-orange-400 to-action-orange-600',
    items: [
      {
        question: 'Quanto custa contratar os serviços da IBX Brasil?',
        answer: 'Nossa remuneração é baseada em comissão paga pelas seguradoras, o que significa que o cliente não paga nada a mais pelo nosso serviço de consultoria. Você paga apenas o prêmio do seguro, e nós trabalhamos para que esse valor seja o mais competitivo possível.'
      },
      {
        question: 'Qual a economia média que os clientes da IBX Brasil obtêm?',
        answer: 'Em média, nossos clientes economizam entre 20% e 40% em relação ao que pagavam antes, sem redução de coberturas. Em muitos casos, além de reduzir o custo, ampliamos as proteções contratadas. Essa economia é resultado da nossa capacidade de negociação e do conhecimento técnico do mercado.'
      },
      {
        question: 'A análise gratuita tem algum compromisso de contratação?',
        answer: 'Não. A análise gratuita é um serviço que oferecemos sem qualquer compromisso. Após a análise, apresentamos um relatório com nossas recomendações e o cliente decide livremente se deseja ou não contratar nossos serviços. Nossa confiança está na qualidade do trabalho que entregamos.'
      },
      {
        question: 'Como a IBX Brasil consegue preços melhores que os que encontro diretamente?',
        answer: 'Temos acesso a tabelas e condições especiais que não estão disponíveis ao público geral, fruto de anos de relacionamento com as seguradoras. Além disso, nossa capacidade de negociação técnica e o volume de negócios que movimentamos nos dão poder de barganha que um cliente individual não teria.'
      }
    ]
  },
  {
    id: 'sinistros',
    label: 'Sinistros',
    icon: 'ri-shield-check-line',
    color: 'from-petrol-600 to-petrol-800',
    items: [
      {
        question: 'O que é recuperação de sinistros?',
        answer: 'Recuperação de sinistros é o processo de acompanhamento e defesa dos interesses do segurado junto à seguradora após um evento coberto pela apólice. Atuamos para garantir que a indenização seja paga de forma justa, completa e no menor prazo possível, evitando negativas indevidas ou pagamentos parciais.'
      },
      {
        question: 'A IBX Brasil me ajuda se meu sinistro for negado pela seguradora?',
        answer: 'Sim. Analisamos tecnicamente a negativa, verificamos se ela é procedente e, caso não seja, atuamos junto à seguradora com argumentação técnica e jurídica para reverter a decisão. Temos histórico comprovado de reversão de negativas indevidas em diversas modalidades de seguro.'
      },
      {
        question: 'Quanto tempo leva para receber a indenização de um sinistro?',
        answer: 'O prazo varia conforme a complexidade do sinistro e a seguradora. Com nosso acompanhamento ativo, conseguimos reduzir significativamente os prazos médios. Para sinistros simples, como colisões de automóvel, o processo pode ser concluído em 5 a 15 dias úteis. Para sinistros mais complexos, trabalhamos para agilizar ao máximo.'
      },
      {
        question: 'Preciso acionar a IBX Brasil imediatamente após um sinistro?',
        answer: 'Quanto antes, melhor. Ao nos acionar imediatamente, podemos orientar sobre os procedimentos corretos desde o início, evitando erros que possam comprometer a indenização. Estamos disponíveis pelo WhatsApp para atendimento urgente em casos de sinistro.'
      },
      {
        question: 'A IBX Brasil acompanha sinistros de seguros que não foram contratados por ela?',
        answer: 'Sim. Oferecemos o serviço de acompanhamento e recuperação de sinistros mesmo para apólices contratadas em outras corretoras. Nesse caso, realizamos uma análise prévia do contrato e apresentamos uma proposta de honorários para a prestação do serviço.'
      }
    ]
  },
  {
    id: 'automovel',
    label: 'Seguro Automóvel',
    icon: 'ri-car-line',
    color: 'from-strategic-green-600 to-petrol-600',
    items: [
      {
        question: 'Quais coberturas são essenciais em um seguro de automóvel?',
        answer: 'As coberturas essenciais incluem: colisão (danos ao próprio veículo), roubo e furto, responsabilidade civil (danos a terceiros), assistência 24 horas e carro reserva. Dependendo do perfil do cliente, também recomendamos cobertura para vidros, acessórios e proteção para motorista e passageiros.'
      },
      {
        question: 'Como é calculado o valor do seguro do meu veículo?',
        answer: 'O cálculo considera diversos fatores: perfil do condutor principal (idade, sexo, histórico de sinistros), características do veículo (modelo, ano, valor de mercado), CEP de pernoite, utilização (particular ou comercial) e coberturas contratadas. Nossa análise identifica quais fatores impactam mais no seu caso e como otimizá-los.'
      },
      {
        question: 'Vale a pena contratar seguro para veículos mais antigos?',
        answer: 'Depende do valor do veículo e do seu perfil de risco. Para veículos com valor de mercado abaixo de R$ 20.000, o custo-benefício do seguro completo pode não ser favorável. Nesses casos, analisamos alternativas como seguro de responsabilidade civil ampliada ou coberturas específicas para roubo e furto.'
      },
      {
        question: 'O que é bônus de seguro e como ele funciona?',
        answer: 'O bônus é um desconto progressivo concedido pelas seguradoras para motoristas que não acionam o seguro. A cada ano sem sinistros, o desconto aumenta, podendo chegar a até 40% do prêmio. Gerenciamos o histórico de bônus dos nossos clientes para garantir que ele seja preservado e aproveitado ao máximo.'
      }
    ]
  },
  {
    id: 'empresarial',
    label: 'Seguros Empresariais',
    icon: 'ri-building-line',
    color: 'from-petrol-700 to-strategic-green-700',
    items: [
      {
        question: 'Quais seguros são indispensáveis para uma empresa?',
        answer: 'Depende do segmento e porte da empresa, mas os mais importantes geralmente são: seguro de vida em grupo (para colaboradores), responsabilidade civil, seguro patrimonial (incêndio, roubo, danos elétricos), seguro de frota (se aplicável) e seguro garantia (para contratos com o poder público). Realizamos um diagnóstico completo para identificar as prioridades do seu negócio.'
      },
      {
        question: 'O que é seguro de vida empresarial e por que contratar?',
        answer: 'O seguro de vida empresarial é uma apólice coletiva que cobre os colaboradores da empresa em caso de morte, invalidez ou doenças graves. Além de ser um benefício valorizado pelos funcionários, pode ser dedutível do imposto de renda da empresa e protege o negócio de passivos trabalhistas em casos de acidentes.'
      },
      {
        question: 'Como funciona a gestão financeira de contratos de seguro?',
        answer: 'Gerenciamos todo o ciclo de vida das apólices da sua empresa: controle de vencimentos, renovações estratégicas, análise de reajustes, renegociação de condições e relatórios periódicos de desempenho. Isso garante que sua empresa nunca fique desprotegida e sempre tenha as melhores condições de mercado.'
      },
      {
        question: 'A IBX Brasil atende micro e pequenas empresas?',
        answer: 'Sim. Temos soluções específicas para micro e pequenas empresas, com coberturas adequadas ao porte e ao orçamento disponível. Muitos pequenos empresários acreditam que seguros empresariais são caros demais, mas nossa análise frequentemente revela opções acessíveis e com excelente custo-benefício.'
      },
      {
        question: 'Como proteger minha empresa contra riscos que não conheço?',
        answer: 'É exatamente para isso que existe a consultoria técnica da IBX Brasil. Realizamos um mapeamento completo dos riscos do seu negócio — incluindo riscos que você talvez não tenha considerado — e apresentamos um plano de gestão de riscos com as coberturas prioritárias e as estratégias de mitigação mais adequadas.'
      }
    ]
  }
];

function FAQAccordionItem({ item, index }: { item: FAQItem; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 ${open ? 'shadow-md' : 'hover:shadow-sm'}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer min-h-[56px]"
        aria-expanded={open}
        aria-controls={`faq-acc-answer-${index}`}
        id={`faq-acc-question-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-petrol-900 leading-snug">{item.question}</span>
        <span className={`w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${open ? 'bg-action-orange-500 text-white rotate-45' : 'bg-petrol-50 text-petrol-600'}`}>
          <i className="ri-add-line text-lg" aria-hidden="true"></i>
        </span>
      </button>
      <div
        id={`faq-acc-answer-${index}`}
        role="region"
        aria-labelledby={`faq-acc-question-${index}`}
        className={`overflow-hidden transition-all duration-400 ease-in-out ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 pb-6 pt-1 bg-white border-t border-gray-100">
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.answer}</p>
        </div>
      </div>
    </div>
  );
}

function useInView() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('geral');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ nome: '', email: '', pergunta: '' });
  const [formErrors, setFormErrors] = useState<{ nome?: string; email?: string; pergunta?: string }>({});
  const [formTouched, setFormTouched] = useState<{ nome?: boolean; email?: boolean; pergunta?: boolean }>({});

  const categoriesAnim = useInView();
  const contentAnim = useInView();
  const ctaAnim = useInView();

  const currentCategory = faqCategories.find(c => c.id === activeCategory)!;

  const filteredItems = searchQuery.trim().length > 1
    ? faqCategories.flatMap(cat => cat.items.filter(item =>
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ))
    : currentCategory.items;

  const isSearching = searchQuery.trim().length > 1;

  const validateFaqField = (name: string, value: string): string => {
    if (name === 'nome') {
      if (!value.trim()) return 'Nome é obrigatório.';
      if (value.trim().length < 3) return 'Nome deve ter ao menos 3 caracteres.';
    }
    if (name === 'email') {
      if (!value.trim()) return 'E-mail é obrigatório.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Informe um e-mail válido.';
    }
    if (name === 'pergunta') {
      if (!value.trim()) return 'Pergunta é obrigatória.';
      if (value.trim().length < 10) return 'Descreva sua dúvida com mais detalhes.';
      if (value.length > 500) return 'Máximo de 500 caracteres.';
    }
    return '';
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formTouched[name as keyof typeof formTouched]) {
      setFormErrors(prev => ({ ...prev, [name]: validateFaqField(name, value) }));
    }
  };

  const handleFormBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormTouched(prev => ({ ...prev, [name]: true }));
    setFormErrors(prev => ({ ...prev, [name]: validateFaqField(name, value) }));
  };

  const getFaqInputClass = (name: 'nome' | 'email' | 'pergunta') => {
    const base = 'w-full px-4 py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none text-sm transition-all';
    if (!formTouched[name]) return `${base} border-gray-300 focus:ring-petrol-400`;
    if (formErrors[name]) return `${base} border-red-400 focus:ring-red-300 bg-red-50`;
    return `${base} border-green-400 focus:ring-green-300 bg-green-50/30`;
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validar todos os campos
    const newErrors: typeof formErrors = {};
    const newTouched = { nome: true, email: true, pergunta: true };
    (['nome', 'email', 'pergunta'] as const).forEach(field => {
      const err = validateFaqField(field, formData[field]);
      if (err) newErrors[field] = err;
    });
    setFormTouched(newTouched);
    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6j3ajufjdhm2lq4t2gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      setSubmitStatus(res.ok ? 'success' : 'error');
      if (res.ok) {
        setFormData({ nome: '', email: '', pergunta: '' });
        setFormTouched({});
        setFormErrors({});
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'Por que minha empresa precisa de consultoria em seguros?',
      answer: 'Muitas empresas contratam seguros sem análise técnica adequada, resultando em coberturas insuficientes ou pagamento excessivo. Nossa consultoria garante proteção adequada ao melhor custo-benefício.'
    },
    {
      question: 'Como funciona a análise de apólices?',
      answer: 'Realizamos uma auditoria completa das suas apólices atuais, identificando gaps de cobertura, cláusulas prejudiciais e oportunidades de economia. O processo leva de 5 a 10 dias úteis.'
    },
    {
      question: 'Qual o investimento em consultoria?',
      answer: 'O investimento varia conforme o porte da empresa e complexidade das apólices. Em média, a economia gerada na renovação cobre o investimento em consultoria.'
    },
    {
      question: 'Vocês trabalham com quais seguradoras?',
      answer: 'Somos independentes e trabalhamos com as principais seguradoras do mercado, garantindo imparcialidade na escolha da melhor opção para sua empresa.'
    },
    {
      question: 'Como é o processo de recuperação de sinistros?',
      answer: 'Atuamos desde a abertura do sinistro até o recebimento da indenização, incluindo documentação, negociação com seguradoras e acompanhamento de perícias.'
    },
    {
      question: 'Quanto tempo leva para implementar as melhorias?',
      answer: 'Após a análise, as melhorias podem ser implementadas na próxima renovação das apólices ou imediatamente, dependendo da urgência e tipo de ajuste necessário.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Perguntas Frequentes sobre Seguros Empresariais | IBX Brasil"
        description="Tire suas dúvidas sobre consultoria em seguros empresariais. Respostas para as perguntas mais frequentes sobre proteção patrimonial, apólices e gestão de contratos."
        canonical="https://ibxbrasil.com.br/faq"
        ogImage="https://readdy.ai/api/search-image?query=frequently%20asked%20questions%20about%20business%20insurance%20consulting%20with%20professional%20advisor%20helping%20clients%20understand%20policies%20in%20modern%20office&width=1200&height=630&seq=ibx-faq-og&orientation=landscape"
        schema={faqSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <PageHero
          badge="CENTRAL DE DÚVIDAS"
          title="Perguntas Frequentes"
          titleHighlight="Frequentes"
          titleHighlightColor="orange"
          subtitle="Encontre respostas claras e objetivas sobre gestão de riscos, contratação profissional de seguros e nossos serviços."
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20business%20consulting%20office%20modern%20interior%20question%20mark%20help%20support%20customer%20service%20deep%20teal%20petrol%20color%20elegant%20corporate%20environment%20bright%20lighting%20knowledge%20center&width=1440&height=700&seq=faq-hero-bg&orientation=landscape"
          overlayIntensity="medium"
          searchBar={{
            placeholder: 'Buscar pergunta...',
            value: searchQuery,
            onChange: setSearchQuery
          }}
          primaryCta={{
            text: 'Falar no WhatsApp',
            href: 'https://wa.me/5577988080505?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20sobre%20seguros.',
            icon: 'ri-whatsapp-line'
          }}
          secondaryCta={{
            text: 'Enviar Mensagem',
            href: '/contato',
            icon: 'ri-mail-line'
          }}
        />

        {/* Category Tabs */}
        {!isSearching && (
          <section className="sticky top-16 sm:top-20 z-30 bg-white border-b border-gray-200 shadow-sm">
            <div
              ref={categoriesAnim.ref as React.RefObject<HTMLDivElement>}
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            >
              <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide" role="tablist" aria-label="Categorias de perguntas">
                {faqCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    role="tab"
                    aria-selected={activeCategory === cat.id}
                    aria-controls={`faq-panel-${cat.id}`}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 min-h-[44px] ${
                      activeCategory === cat.id
                        ? 'bg-petrol-800 text-white shadow-md'
                        : 'text-petrol-600 hover:bg-petrol-50 hover:text-petrol-800'
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Content */}
        <main id="main-content">
          <section className="py-14 sm:py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div ref={contentAnim.ref as React.RefObject<HTMLDivElement>}>

                {/* Search Results Header */}
                {isSearching && (
                  <div className={`mb-8 transition-all duration-500 ${contentAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-petrol-800">{filteredItems.length}</span> resultado{filteredItems.length !== 1 ? 's' : ''} para &ldquo;<span className="text-action-orange-500">{searchQuery}</span>&rdquo;
                    </p>
                  </div>
                )}

                {/* Category Header */}
                {!isSearching && (
                  <div className={`mb-10 transition-all duration-600 ${contentAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${currentCategory.color} shadow-lg`}>
                        <i className={`${currentCategory.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-petrol-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {currentCategory.label}
                        </h2>
                        <p className="text-sm text-gray-500">{currentCategory.items.length} perguntas nesta categoria</p>
                      </div>
                    </div>
                    <div className="h-1 w-16 bg-action-orange-500 rounded-full mt-4"></div>
                  </div>
                )}

                {/* Accordion */}
                {filteredItems.length > 0 ? (
                  <div className="space-y-3" id={!isSearching ? `faq-panel-${activeCategory}` : undefined} role={!isSearching ? 'tabpanel' : undefined}>
                    {filteredItems.map((item, i) => (
                      <div
                        key={i}
                        className={`transition-all duration-500 ${contentAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        style={{ transitionDelay: `${i * 70}ms` }}
                      >
                        <FAQAccordionItem item={item} index={i} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto bg-gray-100 rounded-full mb-4">
                      <i className="ri-search-line text-3xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 text-base">Nenhuma pergunta encontrada para &ldquo;<strong>{searchQuery}</strong>&rdquo;.</p>
                    <p className="text-gray-400 text-sm mt-2">Tente outros termos ou envie sua dúvida abaixo.</p>
                  </div>
                )}

                {/* All Categories Summary (when not searching) */}
                {!isSearching && (
                  <div className={`mt-14 pt-10 border-t border-gray-100 transition-all duration-700 delay-300 ${contentAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <h3 className="text-base font-bold text-petrol-800 mb-5">Explorar outras categorias</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {faqCategories.filter(c => c.id !== activeCategory).map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => { setActiveCategory(cat.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-petrol-300 hover:bg-petrol-50 transition-all text-left cursor-pointer group min-h-[64px]"
                          aria-label={`Ver perguntas sobre ${cat.label}`}
                        >
                          <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br ${cat.color} flex-shrink-0`}>
                            <i className={`${cat.icon} text-base text-white`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-petrol-800 group-hover:text-petrol-900">{cat.label}</p>
                            <p className="text-xs text-gray-400">{cat.items.length} perguntas</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Ask a Question */}
          <section className="py-16 bg-gray-50" aria-labelledby="faq-form-heading">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <div
                ref={ctaAnim.ref as React.RefObject<HTMLDivElement>}
                className={`transition-all duration-700 ${ctaAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="text-center mb-8">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto bg-petrol-800 rounded-xl mb-4 shadow-lg">
                    <i className="ri-chat-3-line text-2xl text-white" aria-hidden="true"></i>
                  </div>
                  <h2 id="faq-form-heading" className="text-2xl sm:text-3xl font-bold text-petrol-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Não encontrou sua resposta?
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Envie sua dúvida e nossa equipe responderá em até 24 horas.
                  </p>
                </div>

                <form id="faq-question-form" onSubmit={handleFormSubmit} data-readdy-form aria-label="Formulário para enviar dúvida" noValidate className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 space-y-5">
                  <div>
                    <label htmlFor="faq-nome" className="block text-sm font-medium text-gray-700 mb-2">Nome <span className="text-red-600" aria-label="obrigatório">*</span></label>
                    <input
                      type="text"
                      id="faq-nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleFormChange}
                      onBlur={handleFormBlur}
                      required
                      aria-required="true"
                      aria-invalid={formTouched.nome ? (formErrors.nome ? 'true' : 'false') : undefined}
                      aria-describedby={formErrors.nome ? 'faq-nome-error' : undefined}
                      placeholder="Seu nome completo"
                      className={getFaqInputClass('nome')}
                      autoComplete="name"
                    />
                    {formTouched.nome && formErrors.nome && (
                      <p id="faq-nome-error" className="field-error" role="alert">
                        <i className="ri-error-warning-line" aria-hidden="true"></i>
                        {formErrors.nome}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="faq-email" className="block text-sm font-medium text-gray-700 mb-2">E-mail <span className="text-red-600" aria-label="obrigatório">*</span></label>
                    <input
                      type="email"
                      id="faq-email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      onBlur={handleFormBlur}
                      required
                      aria-required="true"
                      aria-invalid={formTouched.email ? (formErrors.email ? 'true' : 'false') : undefined}
                      aria-describedby={formErrors.email ? 'faq-email-error' : undefined}
                      placeholder="seu@email.com"
                      className={getFaqInputClass('email')}
                      autoComplete="email"
                    />
                    {formTouched.email && formErrors.email && (
                      <p id="faq-email-error" className="field-error" role="alert">
                        <i className="ri-error-warning-line" aria-hidden="true"></i>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="faq-pergunta" className="block text-sm font-medium text-gray-700 mb-2">Sua Pergunta <span className="text-red-600" aria-label="obrigatório">*</span></label>
                    <textarea
                      id="faq-pergunta"
                      name="pergunta"
                      value={formData.pergunta}
                      onChange={handleFormChange}
                      onBlur={handleFormBlur}
                      required
                      aria-required="true"
                      aria-invalid={formTouched.pergunta ? (formErrors.pergunta ? 'true' : 'false') : undefined}
                      aria-describedby={`faq-pergunta-count${formErrors.pergunta ? ' faq-pergunta-error' : ''}`}
                      rows={4}
                      maxLength={500}
                      placeholder="Descreva sua dúvida com detalhes..."
                      className={`${getFaqInputClass('pergunta')} resize-none`}
                    ></textarea>
                    <div className="flex items-center justify-between mt-1">
                      <span id="faq-pergunta-count" className={`text-xs font-medium ${formData.pergunta.length > 450 ? 'text-red-500' : 'text-gray-400'}`}>
                        {formData.pergunta.length}/500 caracteres
                      </span>
                    </div>
                    {formTouched.pergunta && formErrors.pergunta && (
                      <p id="faq-pergunta-error" className="field-error" role="alert">
                        <i className="ri-error-warning-line" aria-hidden="true"></i>
                        {formErrors.pergunta}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-petrol-800 text-white py-4 rounded-lg font-semibold hover:bg-petrol-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm whitespace-nowrap cursor-pointer min-h-[52px]"
                    aria-label={isSubmitting ? 'Enviando pergunta, aguarde' : 'Enviar pergunta para a equipe IBX Brasil'}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                        Enviando...
                      </span>
                    ) : 'Enviar Pergunta'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2" role="alert" aria-live="polite">
                      <i className="ri-checkbox-circle-line text-green-600 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                      <div><strong>Pergunta enviada!</strong> Responderemos em até 24 horas no seu e-mail.</div>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2" role="alert" aria-live="assertive">
                      <i className="ri-error-warning-line text-red-600 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                      <div><strong>Erro ao enviar.</strong> Tente novamente ou fale pelo WhatsApp.</div>
                    </div>
                  )}
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500 mb-3">Prefere atendimento imediato?</p>
                  <a
                    href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20sobre%20seguros."
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-2 bg-strategic-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-strategic-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer min-h-[48px]"
                    aria-label="Falar no WhatsApp para atendimento imediato"
                  >
                    <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                    Falar no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="py-16 bg-gradient-to-br from-petrol-900 to-petrol-700">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Pronto para contratar com inteligência?
              </h2>
              <p className="text-petrol-200 text-sm sm:text-base mb-8 max-w-xl mx-auto">
                Solicite uma análise gratuita e descubra como a IBX Brasil pode reduzir seus custos e ampliar sua proteção.
              </p>
              
              <div className="mb-8">
                <CountdownTimer location="faq" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/5577988080505"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-action-orange-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-action-orange-600 transition-all shadow-lg whitespace-nowrap cursor-pointer min-h-[52px]"
                  aria-label="Solicitar análise gratuita pelo WhatsApp"
                >
                  <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
                  Análise Gratuita pelo WhatsApp
                </a>
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer min-h-[52px]"
                  aria-label="Enviar mensagem pelo formulário de contato"
                >
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                  Enviar Mensagem
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
